import { Formik, FormikInsurance } from "src/types";
import { AnyObjectSchema } from "yup";
import { useLegalContractor } from "./legal-contractor.hook";
import { InputCheckbox, InputSelect, InputText } from "src/ui/widgets";
import PhoneInput from "react-phone-input-2";
import { lowerLanguage } from "src/i18n";
import { ChangeEvent } from "react";

export function LegalContractor({
  validationSchema,
  formik,
}: {
  formik: Formik<FormikInsurance>;
  validationSchema: AnyObjectSchema;
}) {
  const { handleChange, handleBlur, setFieldValue, setFieldTouched } = formik;

  const {
    legalContractor,
    legalTouched,
    legalErrors,
    companyTypeOptions,
    provincesOptions,
    legalMunicipalitiesOptions,
    legalCapOptions,
    t,
    onChangePhone,
    onSelectICompanyFCNotMatchVAT,
    onSelectLegalProvince,
    onSelectLegalMunicipality,
    onSelectLegalCap,
  } = useLegalContractor({ formik });

  return (
    <>
      <div className="d-block d-md-flex">
        <div className={`col-sm-12 col-md-6 p-0 pe-md-3`}>
          <InputText
            name="contractor.companyName"
            handleChange={handleChange}
            handleBlur={handleBlur}
            validationSchema={validationSchema}
            touched={legalTouched.companyName}
            value={legalContractor.companyName}
            error={legalErrors.companyName}
            label={`${t("data_entry_step_2.insured_company_name")}*`}
            placeholder={t(
              "data_entry_step_2.insured_company_name_placeholder"
            )}
          />
        </div>
        <div className={`col-sm-12 col-md-6 p-0 ps-md-3`}>
          <InputText
            name="contractor.companyFC"
            handleChange={handleChange}
            handleBlur={handleBlur}
            validationSchema={validationSchema}
            touched={legalTouched.companyFC}
            value={legalContractor.companyFC}
            error={legalErrors.companyFC}
            label={`${t("data_entry_step_2.insured_company_cf_vat")}*`}
            placeholder={t(
              "data_entry_step_2.insured_company_cf_vat_placeholder"
            )}
          />
        </div>
      </div>
      <div
        className={`${
          !legalContractor.companyFCNotMatchVAT ? "my-5" : "my-3"
        } d-block d-md-flex align-items-center`}
      >
        <div
          className={`${
            !legalContractor.companyFCNotMatchVAT ? "col-md-12" : "col-md-6"
          } col-sm-12 p-0 `}
        >
          <InputCheckbox
            name="contractor.companyFCNotMatchVAT"
            handleChange={(event: ChangeEvent<HTMLInputElement>) => {
              onSelectICompanyFCNotMatchVAT(event);
              handleChange(event);
            }}
            handleBlur={handleBlur}
            validationSchema={validationSchema}
            touched={legalTouched.companyFCNotMatchVAT}
            value={legalContractor.companyFCNotMatchVAT}
            error={
              legalErrors.companyFCNotMatchVAT &&
              legalTouched.companyFCNotMatchVAT
                ? legalErrors.companyFCNotMatchVAT
                : ""
            }
            label={t("data_entry_step_2.insured_company_fc_not_match_vat")}
          />
        </div>

        {legalContractor.companyFCNotMatchVAT && (
          <div className={`col-sm-12 col-md-6 p-0 ps-md-3`}>
            <InputText
              name="contractor.companyVAT"
              handleChange={handleChange}
              handleBlur={handleBlur}
              validationSchema={validationSchema}
              touched={legalTouched.companyVAT}
              value={legalContractor.companyVAT}
              error={legalErrors.companyVAT}
              label={`${t("data_entry_step_2.insured_company_vat")}*`}
              placeholder={`${t(
                "data_entry_step_2.insured_company_vat_placeholder"
              )}`}
            />
          </div>
        )}
      </div>
      <div className="d-block d-md-flex">
        <div className={`col-sm-12 col-md-6 p-0 pe-md-3`}>
          <InputSelect
            name="contractor.companyLegalProvince"
            validationSchema={validationSchema}
            setFieldValue={setFieldValue}
            handleChange={onSelectLegalProvince}
            options={provincesOptions}
            touched={legalTouched.companyLegalProvince?.value}
            value={legalContractor.companyLegalProvince.id}
            error={legalErrors.companyLegalProvince?.value}
            label={`${t("data_entry_step_2.insured_company_legal_province")}*`}
            placeholder={t(
              "data_entry_step_2.insured_company_legal_province_placeholder"
            )}
          />
        </div>
        <div className={`col-sm-12 col-md-6 p-0 ps-md-3`}>
          <InputSelect
            name="contractor.companyLegalCity"
            setFieldValue={setFieldValue}
            handleChange={onSelectLegalMunicipality}
            options={legalMunicipalitiesOptions}
            disabled={!legalContractor.companyLegalProvince.value}
            validationSchema={validationSchema}
            touched={legalTouched.companyLegalCity?.value}
            value={legalContractor.companyLegalCity.id}
            error={legalErrors.companyLegalCity?.value}
            label={`${t("data_entry_step_2.insured_company_legal_city")}*`}
            placeholder={t(
              "data_entry_step_2.insured_company_legal_city_placeholder"
            )}
          />
        </div>
      </div>
      <div className="d-block d-md-flex">
        <div className={`col-sm-12 col-md-6 p-0 pe-md-3`}>
          <InputText
            name="contractor.companyLegalAddress"
            handleChange={handleChange}
            handleBlur={handleBlur}
            validationSchema={validationSchema}
            touched={legalTouched.companyLegalAddress}
            value={legalContractor.companyLegalAddress}
            error={legalErrors.companyLegalAddress}
            label={`${t("data_entry_step_2.insured_company_legal_address")}*`}
            placeholder={t(
              "data_entry_step_2.insured_company_legal_address_placeholder"
            )}
          />
        </div>
        <div className={`col-sm-12 col-md-6 p-0 ps-md-3`}>
          <InputSelect
            name="contractor.companyLegalCap"
            setFieldValue={setFieldValue}
            handleChange={onSelectLegalCap}
            options={legalCapOptions}
            validationSchema={validationSchema}
            touched={legalTouched.companyLegalCap?.value}
            value={legalContractor.companyLegalCap.id}
            error={legalErrors.companyLegalCap?.value}
            disabled={
              !legalContractor.companyLegalProvince.value ||
              !legalContractor.companyLegalCity.value
            }
            label={`${t("data_entry_step_2.insured_company_legal_cap")}*`}
            placeholder={t(
              "data_entry_step_2.insured_company_legal_cap_placeholder"
            )}
          />
        </div>
      </div>
      <div className="d-block d-md-flex">
        <div
          id="input-contractor.companyPhone"
          className={`${
            !legalTouched.companyPhone?.formattedPhone ||
            !legalErrors.companyPhone?.formattedPhone
              ? "mb-4"
              : "mb-3"
          } col-sm-12 col-md-6 p-0 pe-md-3`}
        >
          <label
            htmlFor="input-companyPhone"
            className="input-text--label d-block mb-md-2 mb-1"
          >
            {`${t("data_entry_step_2.insured_company_legal_phone")}*`}
          </label>
          <div
            className={
              legalTouched.companyPhone?.formattedPhone &&
              legalErrors.companyPhone?.formattedPhone
                ? "phone-style-error"
                : "phone-style"
            }
          >
            <PhoneInput
              country={
                lowerLanguage === "it"
                  ? "it"
                  : lowerLanguage === "en"
                  ? "gb"
                  : "de"
              }
              placeholder={t(
                "data_entry_step_2.insured_company_legal_phone_placeholder"
              )}
              value={legalContractor.companyPhone?.formattedPhone}
              onChange={onChangePhone}
              onBlur={() =>
                setFieldTouched("contractor.companyPhone.formattedPhone", true)
              }
              inputProps={{
                id: "input-companyPhone",
                name: "companyPhone.formattedPhone",
                autoComplete: "tel",
                inputMode: "tel",
              }}
            />
          </div>
          {legalTouched.companyPhone?.formattedPhone &&
            legalErrors.companyPhone?.formattedPhone && (
              <div className="font-14 c-error">
                {legalErrors.companyPhone?.formattedPhone}
              </div>
            )}
        </div>
      </div>
      <div className="d-block d-md-flex">
        <div className={`col-sm-12 col-md-6 p-0 pe-md-3`}>
          <InputSelect
            name="contractor.companyType"
            validationSchema={validationSchema}
            setFieldValue={setFieldValue}
            options={companyTypeOptions}
            touched={legalTouched.companyType}
            value={legalContractor.companyType}
            error={legalErrors.companyType}
            label={`${t("data_entry_step_2.insured_company_type")}`}
            placeholder={t(
              "data_entry_step_2.insured_company_type_placeholder"
            )}
          />
        </div>
      </div>
      <div className="d-block d-md-flex">
        <div className={`col-sm-12 col-md-6 p-0 pe-md-3`}>
          <InputText
            name="contractor.companyEmail"
            handleChange={handleChange}
            handleBlur={handleBlur}
            validationSchema={validationSchema}
            touched={legalTouched.companyEmail}
            value={legalContractor.companyEmail}
            error={legalErrors.companyEmail}
            label={`${t("data_entry_step_2.insured_email")}*`}
            placeholder={t("data_entry_step_2.insured_email_placeholder")}
          />
        </div>
        <div className={`col-sm-12 col-md-6 p-0 ps-md-3`}>
          <InputText
            name="contractor.companyEmailConfirm"
            handleChange={handleChange}
            handleBlur={handleBlur}
            validationSchema={validationSchema}
            touched={legalTouched.companyEmailConfirm}
            error={legalErrors.companyEmailConfirm}
            value={legalContractor.companyEmailConfirm}
            label={`${t("data_entry_step_2.insured_email_confirm")}*`}
            placeholder={t(
              "data_entry_step_2.insured_email_confirm_placeholder"
            )}
            onPaste={(event) => {
              event.preventDefault();
              return false;
            }}
            onCopy={(event) => {
              event.preventDefault();
              return false;
            }}
          />
        </div>
      </div>
    </>
  );
}
